import { useTheme } from '@/store/theme'

import logoTeal from '@/assets/brand/nevtan-ai-logo-teal.png'
import logoWhite from '@/assets/brand/nevtan-ai-logo-white.png'
import markTeal from '@/assets/brand/nevtan-ai-mark-teal.png'
import markWhite from '@/assets/brand/nevtan-ai-mark-white.png'

/**
 * The NevTan AI logo.
 *
 * <p>Four supplied assets, used exactly as delivered: the full lockup and the
 * mark on its own, each in teal for light backgrounds and white for dark ones.
 * Nothing here recolors, crops or redraws them &mdash; a variant is chosen,
 * never generated, so the rendered logo is always the official artwork.
 *
 * <p>The files carry their own transparent margin, so a box of a given height
 * draws the artwork somewhat smaller than that. The sizes callers pass are
 * chosen for how large the logo actually reads, not for the box.
 */

/** Which artwork to use. `auto` follows the application theme. */
export type LogoTone = 'teal' | 'white' | 'auto'

function useTone(tone: LogoTone): 'teal' | 'white' {
  const theme = useTheme((state) => state.theme)
  if (tone !== 'auto') return tone
  return theme === 'dark' ? 'white' : 'teal'
}

interface LogoProps {
  tone?: LogoTone
  className?: string
  /**
   * Empty where the product name is already written next to the logo, so a
   * screen reader does not announce it twice.
   */
  alt?: string
}

/** Mark and wordmark together. The default wherever there is room for it. */
export function BrandLogo({ tone = 'auto', className, alt = 'NevTan AI' }: LogoProps) {
  const resolved = useTone(tone)
  return (
    <img
      src={resolved === 'white' ? logoWhite : logoTeal}
      alt={alt}
      className={className}
      draggable={false}
    />
  )
}

/**
 * Makes the mark fill the box it is given.
 *
 * <p>Both mark files sit in their own transparent margin: the artwork covers
 * 62.9% of the file (measured on each variant — teal wants 1.592, white
 * 1.589) and is inset 18.4% on every side. Left alone the mark draws well
 * inside its box, so it reads small and starts a few pixels in from whatever
 * it is meant to line up with.
 *
 * <p>Scaling by 1/0.629 makes the artwork fill the box exactly without
 * touching layout, so a size class means the size the mark actually appears.
 * The transparent margin overflows the box harmlessly.
 */
const MARK_FILL = 'scale-[1.59]'

/** The mark alone, for places too small or too narrow for the wordmark. */
export function BrandMark({ tone = 'auto', className, alt = 'NevTan AI' }: LogoProps) {
  const resolved = useTone(tone)
  return (
    <img
      src={resolved === 'white' ? markWhite : markTeal}
      alt={alt}
      className={`${className ?? ''} ${MARK_FILL}`}
      draggable={false}
    />
  )
}

/**
 * The mark beside the product name, set as text.
 *
 * <p>The supplied lockup PNG carries its wordmark in teal at a single weight,
 * so it cannot give the suite's treatment: the name in ink with the product
 * word carrying the weight, exactly as NevTan Mail sets "NevTan **Mail**".
 * Setting the name as text rather than pixels is what makes that possible —
 * and it keeps the name selectable, searchable and crisp at any size.
 *
 * <p>The mark itself is still the official artwork, never redrawn.
 */
export function BrandLockup({
  tone = 'auto',
  className,
  markClassName = 'h-8 w-8',
  textClassName = 'text-[19px]',
}: {
  tone?: LogoTone
  /** Wraps the whole lockup. */
  className?: string
  markClassName?: string
  textClassName?: string
}) {
  const resolved = useTone(tone)
  // On the dark footer the ink would disappear, so the name goes white there.
  const ink = resolved === 'white' ? 'text-white' : 'text-brand-ink'

  return (
    <span className={`inline-flex items-center gap-2 ${className ?? ''}`}>
      <img
        src={resolved === 'white' ? markWhite : markTeal}
        alt=""
        className={`${markClassName} shrink-0 ${MARK_FILL}`}
        draggable={false}
      />
      {/* One element, so screen readers and copy-paste get "NevTan AI" as a
          single name rather than two stray words. */}
      <span className={`${ink} ${textClassName} font-semibold leading-none tracking-[-0.01em]`}>
        NevTan <span className="font-extrabold">AI</span>
      </span>
    </span>
  )
}
